// tslint:disable:object-literal-sort-keys
import { MouseCursorData } from "./MouseCursorData";
import { MouseCursor, CSSMouseCursor } from "./MouseCursor";
import { CREATEJS_UTILITIES_LOGGER } from "../../../../../index";
import { getLogger } from "@goodgamestudios/logger";
import { curObjectURLFromCanvas } from "cursor-utilities";
import { currentBrowserInfo } from "../BrowserInfo";
const logger = getLogger(CREATEJS_UTILITIES_LOGGER);

type MouseCursorType =
    { type: "css", cursor: CSSMouseCursor, fallbackCustomCursor: string | null } |
    { type: "custom", cursor: string, fallbackCssCustor: CSSMouseCursor };

/*
 * The global Mouse class controls the presentation of the cursor.
 * You can either use the Mouse.cursor property to set the mouse "icon" to a previously registered graphical one.
 * Or you can set the Mouse.cssCursor to a css cursor style like "pointer or "text".
 *
 * Here is an example of the usage in TextField.ts
 * In Flash, "hiding" the custom cursor was enough - the flash textfield would change the cursor correctly by itself.
 * In the HTML5 / createjs world, the actual HTML <input> is not visible and does not receive (mouse) events directly.
 * Therefor the TextField has to implement changing the cursor. See TextField.onMouseMove() for details.
 *
 * The (only) instance of this class (aka singleton) is accessible via
 *
 * import {Mouse} from "./Mouse"
 * Mouse.instance()
 *
 * At the end of this class declaration, you can find the assignment to the global "window" variable.
 * With declare var we can use the Mouse with typescript support.
 *
 */
export class Mouse {
    private static mouseInstance = new Mouse();

    private _mouseCursor: MouseCursorType;

    private get mouseCursor() {
        return this._mouseCursor;
    }

    private set mouseCursor(nextMouseCursor) {
        const previousCursor = this._mouseCursor;
        this._mouseCursor = nextMouseCursor;

        // update cursor
        document.body.classList.remove(`custom-cursor-${previousCursor.cursor}`);
        document.body.classList.add(`custom-cursor-${nextMouseCursor.cursor}`);
    }

    private _registeredCustomCursors: Set<string> = new Set();

    private _styleSheet: CSSStyleSheet;

    public static instance() {
        return Mouse.mouseInstance;
    }

    /**
     * Register and cache a cursor for later use.
     * @param name name of the cursor, in HTML5, will be part of css, use const from `MouseCorsur` as much as possible unless
     * you really need to custom the cursor name
     * @param cursor cursor data, assets
     */
    public registerCursor(name: string, cursor: MouseCursorData): void {
        if (this._registeredCustomCursors.has(name)) {
            logger.warn(`registerCursor: A custom cursor with name "${name}" is already registered`);
            return;
        }

        this._registeredCustomCursors.add(name);

        const bitmapData = cursor.data[0];
        bitmapData.updateImageData();

        let cursorURL: string;
        if (currentBrowserInfo.isEdge || currentBrowserInfo.isIE) {
            const cursorSize = 32;
            const canvas = document.createElement(`canvas`);
            canvas.width = cursorSize;
            canvas.height = cursorSize;
            const ctx = canvas.getContext(`2d`)!;

            ctx.drawImage(bitmapData.canvas, 0, 0);

            cursorURL = curObjectURLFromCanvas(canvas);
            canvas.width = canvas.height = 0;
        } else {
            cursorURL = bitmapData.canvas.toDataURL();
        }

        const rule = `body.custom-cursor-${name} {cursor:url(${cursorURL}), auto}`;
        this._styleSheet.insertRule(rule, 0);
    }

    /**
     * Hides the custom (!) cursor and shows the fallback css cursor.
     * E.g. used by the Textfield to show a text selection cursor instead of the default arrow cursor
     */
    public hideCustomCursor(): void { // TODO: Verify that this is ever used on purpose
        if (this.mouseCursor.type !== "custom") {
            logger.info(`Not using custom cursor, so nothing to hide`);
            return;
        }

        this.mouseCursor = {
            type: "css",
            cursor: this.mouseCursor.fallbackCssCustor || CSSMouseCursor.NONE,
            fallbackCustomCursor: this.mouseCursor.cursor,
        };
    }

    public showCustomCursor(name: string) {
        if (this.mouseCursor.cursor === name) { return; }
        if (!this._registeredCustomCursors.has(name)) {
            logger.warn(`showCustomCursor: Custom cursor with name "${name}" is not registered`);
        }

        if (this.mouseCursor.type === "custom") {
            this.mouseCursor = {
                type: "custom",
                cursor: name,
                fallbackCssCustor: this.mouseCursor.fallbackCssCustor,
            };
        } else {
            this.mouseCursor = {
                type: "custom",
                cursor: name,
                fallbackCssCustor: this.mouseCursor.cursor,
            };
        }
    }

    /**
     * Sets the css style cursor that will be used whenever the custom(!) cursor is being hidden via Mouse.hide(
     * @param cursor
     */
    public showCssCursor(cursor: CSSMouseCursor) {
        if (this.mouseCursor.type === "custom") {
            this.mouseCursor = {
                type: "css",
                cursor: cursor,
                fallbackCustomCursor: this.mouseCursor.cursor,
            };
        } else {
            if (cursor === this.mouseCursor.cursor) { return; }
            this.mouseCursor = {
                type: "css",
                cursor: cursor,
                fallbackCustomCursor: this.mouseCursor.fallbackCustomCursor,
            };
        }
    }

    private constructor() {
        this._mouseCursor = {
            type: "css",
            cursor: CSSMouseCursor.AUTO,
            fallbackCustomCursor: null,
        };

        const style: HTMLStyleElement = document.createElement("style");
        style.setAttribute("id", "custom-cursor-css");
        // WebKit hack :(
        style.appendChild(document.createTextNode(""));
        // Add the <style> element to the page
        document.head.appendChild(style);
        this._styleSheet = style.sheet as CSSStyleSheet;
        this._styleSheet.insertRule(`body.custom-cursor-none { cursor: none; }`, 0);
        this._styleSheet.insertRule(`body.custom-cursor-${CSSMouseCursor.AUTO} { cursor: auto; }`, 0);
        this._styleSheet.insertRule(`body.custom-cursor-${CSSMouseCursor.TEXT} { cursor: text; }`, 0);
    }

}
